import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { auth, db, storage } from './firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Logo from './components/Logo';
import { useTranslation, Trans } from 'react-i18next';
import html2canvas from 'html2canvas';
import QRCode from 'react-qr-code';
import { Bot, Upload, FileText, Share2, Eye, CheckCircle, QrCode, Link as LinkIcon, X } from 'lucide-react';
import './LinkedInTheme.css';

function MyProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contextData = useOutletContext(); 
  const initialUserProfile = contextData ? contextData.userProfile : null;

  const [user, setUser] = useState(auth.currentUser);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [cvUrl, setCvUrl] = useState('');
  const [cvUri, setCvUri] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [talentCards, setTalentCards] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processMessage, setProcessMessage] = useState('');
  const [cvData, setCvData] = useState(null);
  const [showAllowedFormats, setShowAllowedFormats] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  
  const [internalVtcId, setInternalVtcId] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const [organization, setOrganization] = useState(null);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false); // Org Share Modal
  const [sharingVtc, setSharingVtc] = useState(null);
  const [orgId, setOrgId] = useState('');
  const [shareStatus, setShareStatus] = useState({ loading: false, error: null, success: null });
  const [sharedOrgs, setSharedOrgs] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [showAnsModal, setShowAnsModal] = useState(false);
  const [selectedAnsCard, setSelectedAnsCard] = useState(null);
  const ansBadgeRef = useRef(null);

  // QR Code Modal State
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrVtc, setQrVtc] = useState(null);

  // Consolidated Share Menu State
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [activeShareCard, setActiveShareCard] = useState(null);

  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [showOnboardingMessage, setShowOnboardingMessage] = useState(false);

  useEffect(() => {
    if (initialUserProfile && initialUserProfile.onboardingComplete === false) {
      setShowOnboardingMessage(true);
    }
    if (initialUserProfile && initialUserProfile.orgId) {
        setIsEmployee(true);
        setInternalVtcId(initialUserProfile.internalVtcId);
    }
  }, [initialUserProfile]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const profile = userDoc.data().profile || {};
          setLinkedinUrl(profile.linkedin_url || '');
          setCvUrl(profile.cv_url || '');
          setCvUri(profile.cv_uri || '');
          setAvatarUrl(profile.avatar_url || '');
          setFirstName(profile.firstName || '');
          setLastName(profile.lastName || '');
          setCvData(profile.cvData || null);

          if (profile.orgId) {
            const orgDocRef = doc(db, 'organizations', profile.orgId);
            const orgDoc = await getDoc(orgDocRef);
            if (orgDoc.exists()) {
              setOrganization(orgDoc.data());
            }
          }
        }

        const cardsQuery = query(collection(db, 'talentCards'), where('userId', '==', user.uid));
        const querySnapshot = await getDocs(cardsQuery);
        const cards = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort by createdAt descending (newest first)
        cards.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setTalentCards(cards);

        const orgsByVtc = {};
        for (const card of cards) {
          const sharesQuery = query(collection(db, 'sharedVTCs'), where('vtcId', '==', card.id));
          const sharesSnapshot = await getDocs(sharesQuery);
          const orgIds = sharesSnapshot.docs.map(doc => doc.data().orgId);
          
          if (orgIds.length > 0) {
            const orgPromises = orgIds.map(id => getDoc(doc(db, 'organizations', id)));
            const orgDocs = await Promise.all(orgPromises);
            orgsByVtc[card.id] = orgDocs
              .filter(doc => doc.exists())
              .map(doc => ({ id: doc.id, ...doc.data() }));
          } else {
            orgsByVtc[card.id] = [];
          }
        }
        setSharedOrgs(orgsByVtc);
      }
    };

    fetchProfileData();
  }, [user]);

    const triggerCvProcessing = async (gcsUri, downloadURL) => {

      if (!user) return;

      setIsProcessing(true);

      setProcessMessage(t('myProfile.processingCv'));

      try {

        const processorUrl = `${process.env.REACT_APP_CV_PROCESSOR_URL}/process-cv`;

        const response = await fetch(processorUrl, {

          method: 'POST',

          headers: { 'Content-Type': 'application/json' },

          body: JSON.stringify({ userId: user.uid, gcsUri }),

        });

        

        const responseText = await response.text();

  

        if (!response.ok) {

          let errorDetails = responseText;

          try {

            const errorJson = JSON.parse(responseText);

            errorDetails = errorJson.details || errorJson.error || responseText;

          } catch (e) {}

          throw new Error(`CV processing failed: ${errorDetails}`);

        }

  

        const data = JSON.parse(responseText);

        setCvData(data.cvData); 

        setProcessMessage(t('myProfile.cvProcessedSuccess'));

  

        // Auto-save and redirect if onboarding is incomplete

        if (initialUserProfile && initialUserProfile.onboardingComplete === false) {

          try {

              const userDocRef = doc(db, 'users', user.uid);

              const profileUpdates = {

                'profile.cvData': data.cvData,

                'profile.cv_uri': gcsUri,

                'profile.cv_url': downloadURL,

                'profile.onboardingComplete': true,

                'profile.firstName': firstName || data.cvData.personal_infos?.name?.first_name || '',

                'profile.lastName': lastName || data.cvData.personal_infos?.name?.last_name || '',

                'profile.linkedin_url': linkedinUrl,

                'profile.avatar_url': avatarUrl

              };

              

              await updateDoc(userDocRef, profileUpdates);

              window.location.href = '/chat';

          } catch (saveError) {

              console.error("Auto-save failed:", saveError);

              setProcessMessage(t('myProfile.cvProcessedSaveError'));

          }

        }

  

      } catch (error) {

        setProcessMessage(`Error: ${error.message}`);

        console.error('CV Processing Error:', error);

      } finally {

        setIsProcessing(false);

      }

    };

  

    const handleCvUpload = async (e) => {

      const file = e.target.files[0];

      if (!file || !user) return;

  

      const filePath = `resumes/${user.uid}/${file.name}`;

      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);

      

      const downloadURL = await getDownloadURL(storageRef);

      setCvUrl(downloadURL);

  

      const bucket = storage.app.options.storageBucket;

      const gcsUri = `gs://${bucket}/${filePath}`;

      setCvUri(gcsUri);

      setIsEditing(true);

      await triggerCvProcessing(gcsUri, downloadURL);

    };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    const storageRef = ref(storage, `avatars/${user.uid}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    setAvatarUrl(downloadURL);

    const userDocRef = doc(db, 'users', user.uid);
    await updateDoc(userDocRef, { 'profile.avatar_url': downloadURL });
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    const userDocRef = doc(db, 'users', user.uid);
    
    const profileUpdates = {
      'profile.linkedin_url': linkedinUrl,
      'profile.cv_url': cvUrl,
      'profile.cv_uri': cvUri,
      'profile.avatar_url': avatarUrl,
      'profile.firstName': firstName,
      'profile.lastName': lastName,
      'profile.cvData': cvData,
    };

    if (initialUserProfile && initialUserProfile.onboardingComplete === false) {
      profileUpdates['profile.onboardingComplete'] = true;
    }

    await updateDoc(userDocRef, profileUpdates);
    setIsEditing(false);
    setProcessMessage('');

    if (initialUserProfile && initialUserProfile.onboardingComplete === false) {
      window.location.href = '/chat';
    }
  };

  const handleCvDataChange = (e, key, index = null, field = null) => {
    const { value } = e.target;
    const newCvData = JSON.parse(JSON.stringify(cvData));

    if (index !== null) {
      if (field) {
        newCvData[key][index][field] = value;
      } else {
        newCvData[key][index] = value;
      } 
    } else {
      newCvData[key] = value;
    }
    
    setCvData(newCvData);
    setIsEditing(true);
  };

  const openShareModal = (vtc) => {
    setSharingVtc(vtc);
    setIsShareModalOpen(true);
    setOrgId('');
    setShareStatus({ loading: false, error: null, success: null });
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSharingVtc(null);
  };

  const handleShareVtc = async () => {
    if (!orgId || !sharingVtc) {
      setShareStatus({ ...shareStatus, error: 'Please enter an Organization Code.' });
      return;
    }
    setShareStatus({ loading: true, error: null, success: null });
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to share a VTC.');
      }
      const idToken = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/shareVTC`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ vtcId: sharingVtc.id, orgId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to share VTC.');
      }

      setShareStatus({ loading: false, error: null, success: 'AI Twin shared successfully!' });
      
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (orgDoc.exists()) {
        const newSharedOrgs = { ...sharedOrgs };
        newSharedOrgs[sharingVtc.id].push({ id: orgId, ...orgDoc.data() });
        setSharedOrgs(newSharedOrgs);
      }

      setTimeout(closeShareModal, 2000);
    } catch (error) {
      console.error('Error sharing VTC:', error);
      setShareStatus({ loading: false, error: error.message, success: null });
    }
  };

  const handleUnshareVtc = async (vtcId, orgIdToRemove) => {
    if (!window.confirm(t('unshareConfirmation'))) {
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/unshareVTC`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ vtcId, orgId: orgIdToRemove }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to unshare VTC.');
      }

      setSharedOrgs(prevSharedOrgs => ({
        ...prevSharedOrgs,
        [vtcId]: prevSharedOrgs[vtcId].filter(org => org.id !== orgIdToRemove),
      }));

    } catch (error) {
      console.error('Error unsharing VTC:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleSetInternalProfile = async (vtcId) => {
    if (!user || !isEmployee) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'profile.internalVtcId': vtcId
      });
      setInternalVtcId(vtcId);
    } catch (error) {
      console.error("Error setting internal profile:", error);
      alert("There was an error updating your internal profile. Please try again.");
    }
  };

  const handleShareDigitalTwin = async (vtcId) => {
    try {
      if (ansBadgeRef.current) {
        const canvas = await html2canvas(ansBadgeRef.current, { useCORS: true, scale: 2 });
        await new Promise((resolve) => {
          canvas.toBlob(async (blob) => {
            if (blob) {
              try {
                await navigator.clipboard.write([
                  new ClipboardItem({ 'image/png': blob })
                ]);
                setNotification({ show: true, message: "Badge image copied! Paste it (Ctrl+V) into your LinkedIn post." });
                setTimeout(() => setNotification({ show: false, message: '' }), 5000);
              } catch (err) {
                console.error('Clipboard write failed', err);
                setNotification({ show: true, message: "Could not copy image. Please screenshot manually." });
                setTimeout(() => setNotification({ show: false, message: '' }), 5000);
              }
            }
            resolve();
          });
        });
      }
    } catch (e) {
      console.error("Screenshot failed", e);
    }

    const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${publicUrl}/digital-twin/${vtcId}`;
    const text = t('myProfile.shareDigitalTwinText', "I just verified my AI AI Twin on POD+ ANS — and it’s live!\nTake a look:");
    const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}%20${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank');
  };

  const handleCopyLink = (vtcId) => {
    const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${publicUrl}/digital-twin/${vtcId}`;
    navigator.clipboard.writeText(url).then(() => {
      setNotification({ show: true, message: t('linkCopied', 'Link copied to clipboard!') });
      setTimeout(() => setNotification({ show: false, message: '' }), 3000);
      setIsShareMenuOpen(false);
    }, (err) => {
      console.error('Could not copy text: ', err);
    });
  };

  const handleDownloadQr = async () => {
    const element = document.getElementById('qr-code-container');
    if (element) {
      const canvas = await html2canvas(element);
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `digital-twin-qr-${qrVtc.id}.png`;
      link.click();
    }
  };

  const renderCvData = () => {
    if (!cvData) return null;

    const sectionOrder = ['name', 'workExperience', 'education', 'certifications', 'languages'];
    const fieldOrder = {
      workExperience: ['company', 'role', 'duration'],
      education: ['institution', 'degree', 'year'],
      certifications: ['name', 'url']
    };

    const allKeys = Object.keys(cvData);
    const orderedKeys = sectionOrder.filter(key => allKeys.includes(key));
    const remainingKeys = allKeys.filter(key => !sectionOrder.includes(key)).sort();
    const displayOrder = [...orderedKeys, ...remainingKeys];

    return displayOrder.map(key => {
      const value = cvData[key];
      const keyTitle = t(key, { defaultValue: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) });
  
      if (typeof value === 'string') {
        return (
          <div key={key} className="li-form-stacked">
            <label>{keyTitle}</label>
            <input className="li-input" type="text" value={value} onChange={(e) => handleCvDataChange(e, key)} />
          </div>
        );
      } else if (Array.isArray(value)) {
        return (
          <div key={key} className="cv-data-section" style={{ marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: '#0a66c2' }}>{keyTitle}</h4>
            {value.map((item, index) => (
              <div key={index} className="cv-data-item" style={{ marginBottom: '15px', paddingLeft: '10px', borderLeft: '2px solid #f3f2ef' }}>
                {typeof item === 'string' ? (
                  <div className="li-form-stacked">
                    <input className="li-input" type="text" value={item} onChange={(e) => handleCvDataChange(e, key, index)} />
                  </div>
                ) : (
                  (fieldOrder[key] || Object.keys(item).sort()).map(field => {
                    const fieldValue = item[field];
                    if (fieldValue === null || fieldValue === undefined) return null;
                    
                    return (
                      <div key={field} className="li-form-stacked">
                        <label>{t(field, { defaultValue: field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) })}</label>
                        <input 
                          className="li-input"
                          type="text" 
                          value={fieldValue} 
                          onChange={(e) => handleCvDataChange(e, key, index, field)} 
                        />
                      </div>
                    )
                  })
                )}
              </div>
            ))}
          </div>
        );
      }
      return null;
    });
  };
    
  const handleStartChat = async (e) => {
    e.preventDefault();
    if (initialUserProfile && initialUserProfile.onboardingComplete === false) {
        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                'profile.onboardingComplete': true
            });
            // Update local state if needed
        } catch (error) {
            console.error("Error updating onboarding status:", error);
        }
    }
    navigate('/chat');
  };

  return (
    <div className="li-layout">
      {notification.show && (
        <div className="li-success-msg-small" style={{ position: 'fixed', top: '20px', right: '20px', background: 'white', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 9999 }}>
          {notification.message}
        </div>
      )}
      
      <div className="li-container">
         {/* Left Sidebar */}
         <aside className="li-sidebar-left">
            <div className="li-card li-profile-card">
                <div className="li-profile-background"></div>
                <div className="li-profile-avatar-container">
                    <label htmlFor="avatar-upload" style={{ cursor: 'pointer' }}>
                         <img 
                            src={avatarUrl || defaultAvatar} 
                            alt="Profile" 
                            className="li-avatar"
                        />
                    </label>
                    <input type="file" id="avatar-upload" style={{ display: 'none' }} onChange={handleAvatarUpload} accept="image/*" />
                </div>
                
                <div className="li-member-details">
                     <h3 className="li-member-name">{firstName || lastName ? `${firstName} ${lastName}` : user?.email}</h3>
                     <p className="li-member-headline">{organization ? organization.name : (t('profileDetails') || 'Profile Details')}</p>
                </div>
            </div>

            <div className="li-card">
                {/* Profile Details Edit Section */}
                <div style={{ padding: '12px', textAlign: 'left' }}>
                     <h4 style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>{t('profileDetails')}</h4>
                     <div className="li-form-stacked" style={{ marginBottom: '8px' }}>
                        <input className="li-input" type="text" placeholder={t('firstNamePlaceholder')} value={firstName} onChange={(e) => { setFirstName(e.target.value); setIsEditing(true); }} style={{ padding: '4px 8px', fontSize: '12px' }} />
                    </div>
                    <div className="li-form-stacked" style={{ marginBottom: '8px' }}>
                        <input className="li-input" type="text" placeholder={t('lastNamePlaceholder')} value={lastName} onChange={(e) => { setLastName(e.target.value); setIsEditing(true); }} style={{ padding: '4px 8px', fontSize: '12px' }} />
                    </div>
                    <div className="li-form-stacked">
                        <input className="li-input" type="url" placeholder={t('linkedinPlaceholder')} value={linkedinUrl} onChange={(e) => { setLinkedinUrl(e.target.value); setIsEditing(true); }} style={{ padding: '4px 8px', fontSize: '12px' }} />
                    </div>
                </div>
            </div>

            <div className="li-card">
                {/* Resume Upload Section */}
                <div style={{ padding: '12px', textAlign: 'left' }}>
                    <h4 style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>{t('resume')}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <label htmlFor="cv" className="li-btn-outline-primary" style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '4px 12px' }}>
                            {isProcessing ? t('processing') : <><Upload size={12}/> {t('myProfile.upload', 'Upload')}</>}
                        </label>
                        <input type="file" id="cv" onChange={handleCvUpload} disabled={isProcessing} accept=".pdf,.txt" style={{ display: 'none' }} />
                        
                        {cvUrl && (
                            <a href={cvUrl} target="_blank" rel="noopener noreferrer" className="li-btn-outline-secondary" style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '4px 12px' }}>
                                <FileText size={12}/> {t('myProfile.view', 'View')}
                            </a>
                        )}
                        
                        {/* Skip & Start Chat Button */}
                        <button 
                            onClick={handleStartChat} 
                            className="li-btn-outline-secondary" 
                            style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '4px 12px', border: 'none', color: '#666' }}    
                            title={t('myProfile.skipTooltip')}
                        >
                            {t('myProfile.skipAndStartChat')}
                        </button>
                    </div>
                    {processMessage && <p className="li-success-msg-small" style={{ textAlign: 'left', fontSize: '10px' }}>{processMessage}</p>}
                </div>
                
                {isEditing && (
                    <div style={{ padding: '0 12px 12px' }}>
                        <button className="li-btn-primary" onClick={handleSaveProfile} style={{ width: '100%', fontSize: '12px' }}>{t('saveProfile')}</button>
                    </div>
                )}
             </div>
             


             {/* RBAC Roles */}
            {initialUserProfile?.roles && (
                <div className="li-card" style={{ padding: '12px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                    <strong>{t('myProfile.rbacRoles', 'RBAC Roles')}:</strong> {initialUserProfile.roles.join(', ')}
                </div>
            )}

             {showOnboardingMessage && (
              <div className="li-card" style={{ padding: '16px', backgroundColor: '#e8f0fe', border: '1px solid #0a66c2' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#0a66c2', marginBottom: '8px' }}>{t('onboarding.title')}</h3>
                  <p style={{ fontSize: '12px' }}>{t('onboarding.subtitle')}</p>
              </div>
            )}
         </aside>

         {/* Main Feed */}
         <main className="li-main-feed">
            {/* AI Twins Card (Now Top) */}
            <div className="li-card li-feed-post">
                <div className="li-card-header">
                    <h3>{t('myVTCs')}</h3>
                </div>
                <div className="li-card-body">
                    {talentCards.length === 0 ? (
                         <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                             {t('myProfile.noTwins')} <span onClick={handleStartChat} style={{ color: '#0a66c2', fontWeight: '600', cursor: 'pointer' }}>{t('myProfile.startChat')}</span> {t('myProfile.toCreateOne')}
                         </div>
                    ) : (
                        <ul className="li-list-groups">
                            {talentCards.map(card => (
                                <li key={card.id} style={{ display: 'block', padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                         <div>
                                             <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#0a66c2' }}>
                                                 {card.title}
                                                 {card.ansRegistrationStatus === 'registered' && (
                                                    <button 
                                                      onClick={() => {
                                                        setSelectedAnsCard(card);
                                                        setShowAnsModal(true);
                                                      }} 
                                                      className="ans-lookup-button" 
                                                      title={t('verifiedOnAns')}
                                                      style={{ cursor: 'pointer', marginLeft: '8px', verticalAlign: 'middle', padding: 0, display: 'inline-flex' }}
                                                    >
                                                        <Bot size={16} style={{ color: '#057642' }} />
                                                    </button>
                                                 )}
                                             </h4>
                                             <span style={{ fontSize: '12px', color: '#666' }}>{t('created')}: {card.createdAt?.toDate().toLocaleDateString()}</span>
                                         </div>
                                         <div style={{ display: 'flex', gap: '8px' }}>
                                            <Link to={`/talent-card/${card.id}`} className="li-icon-btn-ghost" style={{ position: 'static', color: '#666' }} title={t('myProfile.viewTwin')}>
                                                <Eye size={20} />
                                            </Link>
                                            <button onClick={() => { setActiveShareCard(card); setIsShareMenuOpen(true); }} className="li-icon-btn-ghost" style={{ position: 'static', color: '#666' }} title={t('shareMenuTitle')}>
                                                <Share2 size={20} />
                                            </button>
                                         </div>
                                     </div>

                                     <div style={{ marginBottom: '12px' }}>
                                        {card.skills?.slice(0, 5).map((skill, i) => (
                                            <span key={i} style={{ display: 'inline-block', backgroundColor: '#f3f2ef', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', margin: '0 4px 4px 0', color: '#666' }}>
                                                {skill}
                                            </span>
                                        ))}
                                     </div>

                                     {sharedOrgs[card.id] && sharedOrgs[card.id].length > 0 && (
                                          <div style={{ fontSize: '12px', backgroundColor: '#e8f0fe', padding: '8px', borderRadius: '4px' }}>
                                              <strong>{t('sharedWith')}: </strong>
                                              {sharedOrgs[card.id].map(org => (
                                                  <span key={org.id} style={{ display: 'inline-flex', alignItems: 'center', marginRight: '8px', fontWeight: '500' }}>
                                                      {org.name}
                                                      <button onClick={() => handleUnshareVtc(card.id, org.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cc1016', marginLeft: '4px', fontSize: '14px' }}>&times;</button>
                                                  </span>
                                              ))}
                                          </div>
                                     )}

                                     {isEmployee && internalVtcId !== card.id && (
                                         <button onClick={() => handleSetInternalProfile(card.id)} className="li-btn-outline-secondary" style={{ marginTop: '12px', fontSize: '12px' }}>
                                            {t('setAsInternalProfile')}
                                         </button>
                                     )}
                                     {isEmployee && internalVtcId === card.id && (
                                          <span style={{ display: 'inline-block', marginTop: '12px', fontSize: '12px', color: '#057642', fontWeight: '600', padding: '4px 8px', border: '1px solid #057642', borderRadius: '4px' }}>
                                              {t('workProfile')}
                                          </span>
                                     )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Extracted Data Card */}
            {cvData && (
                 <div className="li-card li-create-post-card">
                    <div className="li-card-header">
                        <h3>{t('extractedCvData')}</h3>
                        <div className="info-tooltip" style={{ marginLeft: '8px', cursor: 'help' }} title={t('cvDataInfo')}>ℹ️</div>
                    </div>
                    <div className="li-card-body">
                        {renderCvData()}
                    </div>
                 </div>
            )}
         </main>

         {/* Right Sidebar */}
         <aside className="li-sidebar-right">
             <div className="li-card li-news-card">
                <div className="li-card-header">
                    <h3>{t('myProfile.quickActions')}</h3>
                </div>
                <div className="li-card-body">
                     <Link to="/simulations" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '10px', boxSizing: 'border-box' }}>
                        Activate Agent
                     </Link>
                      <Link to="/agent-performance" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '10px', boxSizing: 'border-box' }}>
                        Agent Analytics
                     </Link>
                     <Link 
                        to={internalVtcId ? `/digital-twin/${internalVtcId}` : (talentCards.length > 0 ? `/digital-twin/${talentCards[0].id}` : '/chat')} 
                        className="li-btn-outline-primary" 
                        style={{ width: '100%', textAlign: 'center', display: 'block', boxSizing: 'border-box' }}
                     >
                        {t('talkToTwin', 'Talk to AI Twin')}
                     </Link>
                </div>
             </div>
         </aside>
      </div>

      {/* Share Menu Modal */}
      {isShareMenuOpen && activeShareCard && (
        <div className="share-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={() => setIsShareMenuOpen(false)}>
          <div className="li-card" style={{ width: '350px', maxWidth: '90%', margin: 0, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
             <div className="li-card-header" style={{ borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>{t('shareMenuTitle', 'Share AI Twin')}</h3>
                <button onClick={() => setIsShareMenuOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: 'rgba(0,0,0,0.6)' }}>
                  <X size={20} />
                </button>
             </div>
             <div className="li-card-body" style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Option 1: Share with Organization */}
                <button 
                  className="li-btn-outline-secondary" 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.15)' }}
                  onClick={() => { setIsShareMenuOpen(false); openShareModal(activeShareCard); }}
                >
                  <Share2 size={20} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(0,0,0,0.75)' }}>{t('shareOptionOrg', 'Share with Organization')}</span>
                </button>

                {/* Option 2: Share on LinkedIn */}
                <button 
                   className="li-btn-outline-secondary"
                   style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.15)' }}
                   onClick={() => { setIsShareMenuOpen(false); handleShareDigitalTwin(activeShareCard.id); }}
                >
                  <svg viewBox="0 0 32 32" fill="currentColor" width="20px" height="20px">
                      <path d="M26,2H6C3.8,2,2,3.8,2,6v20c0,2.2,1.8,4,4,4h20c2.2,0,4-1.8,4-4V6C30,3.8,28.2,2,26,2z M12,26H8V12h4V26z M10,10 c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S11.1,10,10,10z M26,26h-4v-8c0-1.1-0.4-2-1.5-2S19,17.1,19,18v8h-4V12h4v2 c0.6-1,1.9-1.5,2.5-1.5c1.5,0,3.5,1,3.5,4V26z"></path>
                  </svg>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(0,0,0,0.75)' }}>{t('shareOptionLinkedIn', 'Share on LinkedIn')}</span>
                </button>

                 {/* Option 3: Share via QR Code */}
                 <button 
                   className="li-btn-outline-secondary"
                   style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.15)' }}
                   onClick={() => { setIsShareMenuOpen(false); setQrVtc(activeShareCard); setQrModalOpen(true); }}
                >
                  <QrCode size={20} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(0,0,0,0.75)' }}>{t('shareOptionQr', 'Share via QR Code')}</span>
                </button>

                 {/* Option 4: Copy Link */}
                 <button 
                   className="li-btn-outline-secondary"
                   style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.15)' }}
                   onClick={() => handleCopyLink(activeShareCard.id)}
                >
                  <LinkIcon size={20} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(0,0,0,0.75)' }}>{t('shareOptionCopyLink', 'Copy Link')}</span>
                </button>

                <button 
                  onClick={() => setIsShareMenuOpen(false)} 
                  className="li-btn-outline-secondary"
                  style={{ marginTop: '8px', width: '100%', justifyContent: 'center', borderColor: 'rgba(0,0,0,0.6)', color: 'rgba(0,0,0,0.75)' }}
                >
                  Cancel
                </button>
             </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {isShareModalOpen && (
        <div className="share-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="share-modal" style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
            <div className="share-modal-header" style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '8px' }}>Share "{sharingVtc?.title}"</h3>
              <p style={{ fontSize: '14px', color: '#666' }}>Enter the unique code provided by your organization.</p>
            </div>
            <div className="share-modal-body">
              {shareStatus.success ? (
                <div className="li-success-msg">{shareStatus.success}</div>
              ) : (
                <>
                  <div className="li-form-stacked">
                    <label htmlFor="orgId">{t('myProfile.orgCode')}</label>
                    <input 
                      className="li-input"
                      type="text" 
                      id="orgId"
                      value={orgId} 
                      onChange={(e) => setOrgId(e.target.value)} 
                      placeholder={t('adminDashboard.orgIdPlaceholder')} 
                    />
                  </div>
                  {shareStatus.loading && <div className="spinner">{t('loading')}</div>}
                  {shareStatus.error && <div className="li-error-msg">{shareStatus.error}</div>}
                </>
              )}
            </div>
            {!shareStatus.success && (
              <div className="share-modal-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={closeShareModal} className="li-btn-outline-secondary">{t('myProfile.cancel')}</button>
                <button onClick={handleShareVtc} className="li-btn-primary" disabled={shareStatus.loading}>
                  {shareStatus.loading ? t('myProfile.sharing') : t('myProfile.shareTwin')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {qrModalOpen && qrVtc && (
        <div className="share-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={() => setQrModalOpen(false)}>
          <div className="share-modal" style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', width: '300px', maxWidth: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>{t('myProfile.shareViaQr')}</h3>
            <div id="qr-code-container" style={{ padding: '10px', backgroundColor: 'white' }}>
              <QRCode
                value={`${process.env.REACT_APP_PUBLIC_URL || window.location.origin}/digital-twin/${qrVtc.id}`}
                size={200}
                level={"H"}
              />
            </div>
            <p style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px', color: '#666' }}>{t('myProfile.scanQrCode')}</p>
            <button onClick={handleDownloadQr} className="li-btn-outline-primary" style={{ marginTop: '16px', width: '100%' }}>
              {t('myProfile.downloadQr')}
            </button>
            <button onClick={() => setQrModalOpen(false)} className="li-btn-primary" style={{ marginTop: '8px', width: '100%' }}>
              {t('myProfile.close')}
            </button>
          </div>
        </div>
      )}

      {showAnsModal && selectedAnsCard && (
        <div className="ans-modal-overlay" onClick={() => {
          setShowAnsModal(false);
          setSelectedAnsCard(null);
        }}>
          <div className="ans-badge-container" ref={ansBadgeRef} onClick={(e) => e.stopPropagation()}>
            <div className="ans-badge-header">
              <Logo className="logo" />
            </div>
            <div className="ans-badge-body">
              <div className="avatar-placeholder">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="User avatar" className="ans-avatar-img" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                )}
              </div>
              <div className="ans-agent-info">
                <h2 className="ans-agent-name">{selectedAnsCard.name}</h2>
                <p className="ans-agent-title">{selectedAnsCard.title}</p>
              </div>
            </div>
            <div className="ans-badge-footer">
              <div className="ans-info-item" style={{ marginBottom: '20px' }}>
                <label>{t('myProfile.agentId')}</label>
                <code>{`pod.${selectedAnsCard.userId}.${selectedAnsCard.id}.ans`}</code>
              </div>
              <div className="ans-badge-verification">
                <CheckCircle width="20" height="20" style={{ marginRight: '8px', color: '#057642' }} />
                <span>{t('verifiedOnAns')}</span>
              </div>
              <div style={{ marginTop: '16px' }}>
                <button 
                  onClick={() => handleShareDigitalTwin(selectedAnsCard.id)}
                  className="li-btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'center' }}
                >
                  <svg viewBox="0 0 32 32" fill="currentColor" width="20px" height="20px">
                    <path d="M26,2H6C3.8,2,2,3.8,2,6v20c0,2.2,1.8,4,4,4h20c2.2,0,4-1.8,4-4V6C30,3.8,28.2,2,26,2z M12,26H8V12h4V26z M10,10 c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S11.1,10,10,10z M26,26h-4v-8c0-1.1-0.4-2-1.5-2S19,17.1,19,18v8h-4V12h4v2 c0.6-1,1.9-1.5,2.5-1.5c1.5,0,3.5,1,3.5,4V26z"></path>
                  </svg>
                  {t('myProfile.shareOnLinkedIn', 'Share on LinkedIn')}
                </button>
              </div>
            </div>
            <div className="footer-note">
              <p className="pod-description"><strong>POD+</strong> {t('podDescription')}</p>
              <p className="powered-by-ans">
                <Trans i18nKey="poweredByAns">
                  Powered by <strong>ANS (Agent Network System)</strong>.
                </Trans>
                {' '}
                <a href="https://www.gclouds.co.uk/ans" target="_blank" rel="noopener noreferrer">{t('learnMore')}</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyProfile;
